/**
 * Initializes the quote request form on the quote page.
 *
 * On a successful submit the form is replaced with a confirmation
 * that lists the values that were sent. Cart line items are attached
 * as hidden fields before submit, then the cart is cleared.
 */
const initQuoteForm = () => {
  /** @type {HTMLElement | null} */
  const root = document.querySelector('.quote-form');

  if (!root || root.dataset.quoteFormInitialized === 'true') return;

  const STORAGE_KEY = 'quote-form-submission';
  const SNAPSHOT_TTL_MS = 10 * 60 * 1000;
  const SUBMIT_TIMEOUT_MS = 20000;

  /**
   * @typedef {Object} QuoteCartItem
   * @property {number} quantity
   * @property {string=} product_title
   * @property {string=} title
   * @property {string=} variant_title
   * @property {number|string=} line_price
   */

  /**
   * @typedef {Object} QuoteCart
   * @property {QuoteCartItem[]} items
   * @property {number|string=} total_price
   */

  /**
   * @typedef {Object} QuoteSnapshotField
   * @property {string} label
   * @property {string} value
   */

  /**
   * @typedef {Object} QuoteSnapshot
   * @property {QuoteSnapshotField[]} fields
   * @property {string[]} items
   */

  /** @type {HTMLFormElement | null} */
  const quoteForm = root.querySelector('#QuoteRequestForm');
  /** @type {HTMLInputElement | null} */
  const startDateField = root.querySelector('#QuoteStartDate');
  /** @type {HTMLInputElement | null} */
  const endDateField = root.querySelector('#QuoteEndDate');
  /** @type {HTMLElement | null} */
  const formFields = root.querySelector('.quote-form__form-fields');
  /** @type {HTMLElement | null} */
  const confirmation = root.querySelector('.quote-form__confirmation');
  /** @type {HTMLElement | null} */
  const confirmationDetails = root.querySelector('.quote-form__confirmation-details');
  /** @type {HTMLElement | null} */
  const title = root.querySelector('.quote-form__title');
  /** @type {HTMLElement | null} */
  const errorMessage = root.querySelector('.quote-form__error');
  /** @type {HTMLIFrameElement | null} */
  const submitFrame = root.querySelector('.quote-form__submit-frame');
  /** @type {HTMLButtonElement | null} */
  const downloadPdfButton = root.querySelector('.js-download-quote-pdf');

  /** @type {QuoteSnapshot | null} */
  let latestSnapshot = null;

  /**
   * Escapes a string for use inside a PDF literal `(...)`.
   * @param {string} value
   * @returns {string}
   */
  const escapePdfString = (value) =>
    value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

  /**
   * Converts text to PDF-safe ASCII (Helvetica has no Unicode cmap here).
   * @param {string} value
   * @returns {string}
   */
  const toPdfText = (value) =>
    value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, '?');

  /**
   * @param {string} hex
   * @param {[number, number, number]} fallback
   * @returns {[number, number, number]}
   */
  const hexToRgb = (hex, fallback) => {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');

    if (!match) return fallback;

    return [parseInt(match[1], 16) / 255, parseInt(match[2], 16) / 255, parseInt(match[3], 16) / 255];
  };

  /**
   * Approximate glyph width for Helvetica in PDF text units.
   * @param {string} text
   * @param {number} fontSize
   * @returns {number}
   */
  const measureText = (text, fontSize) => text.length * fontSize * 0.5;

  /**
   * Wraps text to a maximum width in points.
   * @param {string} text
   * @param {number} fontSize
   * @param {number} maxWidth
   * @returns {string[]}
   */
  const wrapTextToWidth = (text, fontSize, maxWidth) => {
    const normalized = text.replace(/\s+/g, ' ').trim();

    if (!normalized) return [''];

    const words = normalized.split(' ');
    /** @type {string[]} */
    const lines = [];
    let current = '';

    for (const word of words) {
      const next = current ? `${current} ${word}` : word;

      if (measureText(next, fontSize) <= maxWidth) {
        current = next;
      } else {
        if (current) lines.push(current);
        current = word;
      }
    }

    if (current) lines.push(current);

    return lines.length ? lines : [''];
  };

  /**
   * Builds a styled multi-page PDF that mirrors the confirmation layout.
   * @param {QuoteSnapshot} snapshot
   * @returns {Blob}
   */
  const buildQuotePdf = (snapshot) => {
    const pageWidth = 595;
    const pageHeight = 842;
    const marginX = 48;
    const contentWidth = pageWidth - marginX * 2;
    const labelWidth = contentWidth * 0.34;
    const valueWidth = contentWidth - labelWidth - 16;
    const marginBottom = 56;
    const rowGap = 14;
    const lineHeight = 14;

    const pdfTitle = downloadPdfButton?.dataset.pdfTitle || 'Quote request overview';
    const shopName = downloadPdfButton?.dataset.pdfShop || '';
    const successMessage =
      downloadPdfButton?.dataset.pdfSuccess ||
      'Thank you! We will get back to you as soon as possible.';
    const itemsLabel = confirmationDetails?.dataset.itemsLabel || 'Requested items';
    const generatedAt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date());

    const fallbackAccent = hexToRgb(downloadPdfButton?.dataset.pdfAccent || '', [0, 0, 0]);
    const fallbackHeading = hexToRgb(downloadPdfButton?.dataset.pdfHeading || '', [0, 0, 0]);
    const fallbackText = hexToRgb(downloadPdfButton?.dataset.pdfText || '', [0.2, 0.2, 0.2]);
    const muted = hexToRgb(downloadPdfButton?.dataset.pdfMuted || '', [0.4, 0.4, 0.4]);
    const border = hexToRgb(downloadPdfButton?.dataset.pdfBorder || '', [0.9, 0.9, 0.9]);

    // Prefer live theme colors when available so the PDF tracks the confirmation page.
    const rootStyles = getComputedStyle(root);
    const brandRaw =
      rootStyles.getPropertyValue('--color-primary-button-background').trim() ||
      rootStyles.getPropertyValue('--color-foreground-heading').trim();
    const headingRaw = rootStyles.getPropertyValue('--color-foreground-heading').trim();
    const textRaw = rootStyles.getPropertyValue('--color-foreground').trim();

    /**
     * @param {string} cssColor
     * @param {[number, number, number]} fallback
     * @returns {[number, number, number]}
     */
    const cssToRgb = (cssColor, fallback) => {
      if (!cssColor) return fallback;

      const hexMatch = cssColor.match(/#([a-f\d]{6})/i);
      if (hexMatch) return hexToRgb(`#${hexMatch[1]}`, fallback);

      const rgbMatch = cssColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
      if (rgbMatch) {
        return [Number(rgbMatch[1]) / 255, Number(rgbMatch[2]) / 255, Number(rgbMatch[3]) / 255];
      }

      return fallback;
    };

    const accent = cssToRgb(brandRaw, fallbackAccent);
    const heading = cssToRgb(headingRaw, fallbackHeading);
    const text = cssToRgb(textRaw, fallbackText);
    /**
     * @typedef {Object} PdfDrawOp
     * @property {'fillRect'|'strokeLine'|'text'|'check'|'pageBreak'} type
     * @property {number=} x
     * @property {number=} y
     * @property {number=} w
     * @property {number=} h
     * @property {number=} x2
     * @property {number=} y2
     * @property {string=} value
     * @property {number=} size
     * @property {'regular'|'bold'=} weight
     * @property {[number, number, number]=} color
     */

    /** @type {PdfDrawOp[]} */
    const ops = [];
    let y = pageHeight - 36;

    /**
     * @param {number} amount
     */
    const ensureSpace = (amount) => {
      if (y - amount < marginBottom) {
        ops.push({ type: 'pageBreak' });
        y = pageHeight - 48;
      }
    };

    // Top accent bar
    ops.push({ type: 'fillRect', x: 0, y: pageHeight - 10, w: pageWidth, h: 10, color: accent });

    y = pageHeight - 42;

    if (shopName) {
      ops.push({
        type: 'text',
        x: marginX,
        y,
        value: toPdfText(shopName).toUpperCase(),
        size: 9,
        weight: 'bold',
        color: accent,
      });
      y -= 22;
    }

    ops.push({
      type: 'text',
      x: marginX,
      y,
      value: toPdfText(pdfTitle),
      size: 20,
      weight: 'bold',
      color: heading,
    });
    y -= 18;

    ops.push({
      type: 'text',
      x: marginX,
      y,
      value: toPdfText(generatedAt),
      size: 10,
      weight: 'regular',
      color: muted,
    });
    y -= 28;

    // Success block
    ensureSpace(48);
    ops.push({ type: 'check', x: marginX, y: y - 2, color: accent });
    const successLines = wrapTextToWidth(toPdfText(successMessage), 11, contentWidth - 28);

    for (const [index, line] of successLines.entries()) {
      ops.push({
        type: 'text',
        x: marginX + 28,
        y: y - index * lineHeight,
        value: line,
        size: 11,
        weight: 'regular',
        color: text,
      });
    }

    y -= Math.max(successLines.length, 1) * lineHeight + 18;

    // Top divider
    ensureSpace(8);
    ops.push({
      type: 'strokeLine',
      x: marginX,
      y,
      x2: marginX + contentWidth,
      y2: y,
      color: border,
    });
    y -= 20;

    /**
     * @param {string} label
     * @param {string} value
     * @param {boolean=} emphasizeLabel
     */
    const drawRow = (label, value, emphasizeLabel = false) => {
      const labelLines = wrapTextToWidth(toPdfText(label), 10, labelWidth);
      const valueLines = wrapTextToWidth(toPdfText(value), 10, valueWidth);
      const rowHeight = Math.max(labelLines.length, valueLines.length) * lineHeight;

      ensureSpace(rowHeight + rowGap);

      for (const [index, line] of labelLines.entries()) {
        ops.push({
          type: 'text',
          x: marginX,
          y: y - index * lineHeight,
          value: line,
          size: 10,
          weight: emphasizeLabel || index === 0 ? 'bold' : 'regular',
          color: heading,
        });
      }

      for (const [index, line] of valueLines.entries()) {
        ops.push({
          type: 'text',
          x: marginX + labelWidth + 16,
          y: y - index * lineHeight,
          value: line,
          size: 10,
          weight: 'regular',
          color: text,
        });
      }

      y -= rowHeight + rowGap;
    };

    for (const field of snapshot.fields) {
      drawRow(field.label || '', field.value || '');
    }

    if (snapshot.items.length) {
      ensureSpace(36);
      ops.push({
        type: 'strokeLine',
        x: marginX,
        y,
        x2: marginX + contentWidth,
        y2: y,
        color: border,
      });
      y -= 22;

      ops.push({
        type: 'text',
        x: marginX,
        y,
        value: toPdfText(itemsLabel),
        size: 11,
        weight: 'bold',
        color: heading,
      });
      y -= 16;

      for (const item of snapshot.items) {
        const valueLines = wrapTextToWidth(toPdfText(item), 10, contentWidth - 20);
        const rowHeight = valueLines.length * lineHeight;

        ensureSpace(rowHeight + rowGap);
        ops.push({
          type: 'fillRect',
          x: marginX,
          y: y - rowHeight + 4,
          w: 3,
          h: rowHeight + 4,
          color: accent,
        });

        for (const [index, line] of valueLines.entries()) {
          ops.push({
            type: 'text',
            x: marginX + 14,
            y: y - index * lineHeight,
            value: line,
            size: 10,
            weight: 'regular',
            color: text,
          });
        }

        y -= rowHeight + rowGap;
      }
    }

    // Bottom divider + footer
    ensureSpace(36);
    ops.push({
      type: 'strokeLine',
      x: marginX,
      y,
      x2: marginX + contentWidth,
      y2: y,
      color: border,
    });
    y -= 18;
    ops.push({
      type: 'text',
      x: marginX,
      y,
      value: toPdfText(shopName ? `${shopName} · ${pdfTitle}` : pdfTitle),
      size: 8,
      weight: 'regular',
      color: muted,
    });

    // Accent footer bar
    ops.push({ type: 'fillRect', x: 0, y: 0, w: pageWidth, h: 8, color: accent });

    /** @type {PdfDrawOp[][]} */
    const pages = [[]];

    for (const op of ops) {
      if (op.type === 'pageBreak') {
        pages.push([]);
        continue;
      }

      pages[pages.length - 1].push(op);
    }

    /** @type {string[]} */
    const objects = [];
    const addObject = (body) => {
      objects.push(body);
      return objects.length;
    };

    const fontRegular = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
    const fontBold = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
    /** @type {number[]} */
    const pageObjectIds = [];

    /**
     * @param {[number, number, number]} color
     * @returns {string}
     */
    const rgb = (color) =>
      `${color[0].toFixed(3)} ${color[1].toFixed(3)} ${color[2].toFixed(3)}`;

    for (const [pageIndex, pageOps] of pages.entries()) {
      /** @type {string[]} */
      const streamLines = [];

      // Repeat accent bar on continuation pages
      if (pageIndex > 0) {
        streamLines.push(`${rgb(accent)} rg`);
        streamLines.push(`0 ${pageHeight - 10} ${pageWidth} 10 re f`);
        streamLines.push(`${rgb(accent)} rg`);
        streamLines.push(`0 0 ${pageWidth} 8 re f`);
      }

      for (const op of pageOps) {
        if (op.type === 'fillRect' && op.color && op.x != null && op.y != null && op.w != null && op.h != null) {
          streamLines.push(`${rgb(op.color)} rg`);
          streamLines.push(`${op.x} ${op.y} ${op.w} ${op.h} re f`);
        }

        if (
          op.type === 'strokeLine' &&
          op.color &&
          op.x != null &&
          op.y != null &&
          op.x2 != null &&
          op.y2 != null
        ) {
          streamLines.push(`${rgb(op.color)} RG`);
          streamLines.push('1 w');
          streamLines.push(`${op.x} ${op.y} m ${op.x2} ${op.y2} l S`);
        }

        if (op.type === 'check' && op.color && op.x != null && op.y != null) {
          const size = 14;
          streamLines.push(`${rgb(op.color)} rg`);
          streamLines.push(`${op.x} ${op.y - 2} ${size} ${size} re f`);
          streamLines.push('1 1 1 RG');
          streamLines.push('1.5 w');
          streamLines.push(
            `${op.x + 3} ${op.y + 4} m ${op.x + 6} ${op.y + 1} l ${op.x + 11} ${op.y + 8} l S`
          );
        }

        if (op.type === 'text' && op.value != null && op.x != null && op.y != null && op.color) {
          const fontRef = op.weight === 'bold' ? '/F2' : '/F1';
          const size = op.size || 10;
          streamLines.push('BT');
          streamLines.push(`${fontRef} ${size} Tf`);
          streamLines.push(`${rgb(op.color)} rg`);
          streamLines.push(`${op.x} ${op.y} Td`);
          streamLines.push(`(${escapePdfString(op.value)}) Tj`);
          streamLines.push('ET');
        }
      }

      // Always draw footer accent on first page ops already include it; ensure every page has it
      if (pageIndex === 0) {
        // footer rect already in ops
      } else if (!pageOps.some((op) => op.type === 'fillRect' && op.y === 0)) {
        streamLines.push(`${rgb(accent)} rg`);
        streamLines.push(`0 0 ${pageWidth} 8 re f`);
      }

      const stream = `${streamLines.join('\n')}\n`;
      const contentObject = addObject(
        `<< /Length ${stream.length} >>\nstream\n${stream}endstream`
      );
      const pageObject = addObject(
        `<< /Type /Page /Parent PAGES_REF /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentObject} 0 R /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> >>`
      );
      pageObjectIds.push(pageObject);
    }

    const kids = pageObjectIds.map((id) => `${id} 0 R`).join(' ');
    const pagesObject = addObject(
      `<< /Type /Pages /Kids [${kids}] /Count ${pageObjectIds.length} >>`
    );
    const catalogObject = addObject(`<< /Type /Catalog /Pages ${pagesObject} 0 R >>`);

    const resolvedObjects = objects.map((body) =>
      body.replace(/PAGES_REF/g, `${pagesObject} 0 R`)
    );

    /** @type {string[]} */
    const pdfParts = ['%PDF-1.4\n'];
    /** @type {number[]} */
    const offsets = [0];
    let cursor = pdfParts[0].length;

    for (const [index, body] of resolvedObjects.entries()) {
      const objectNumber = index + 1;
      const objectText = `${objectNumber} 0 obj\n${body}\nendobj\n`;
      offsets.push(cursor);
      pdfParts.push(objectText);
      cursor += objectText.length;
    }

    const xrefOffset = cursor;
    let xref = `xref\n0 ${resolvedObjects.length + 1}\n`;
    xref += '0000000000 65535 f \n';

    for (let index = 1; index <= resolvedObjects.length; index += 1) {
      xref += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
    }

    pdfParts.push(xref);
    pdfParts.push(
      `trailer\n<< /Size ${resolvedObjects.length + 1} /Root ${catalogObject} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`
    );

    return new Blob(pdfParts, { type: 'application/pdf' });
  };

  /**
   * @param {QuoteSnapshot} snapshot
   */
  const downloadQuotePdf = (snapshot) => {
    const blob = buildQuotePdf(snapshot);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filenameBase = downloadPdfButton?.dataset.pdfFilename || 'quote-request';
    const dateStamp = toLocalISODate(new Date());

    link.href = url;
    link.download = `${filenameBase}-${dateStamp}.pdf`;
    link.rel = 'noopener';
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /**
   * @param {Date} date
   * @returns {string}
   */
  const toLocalISODate = (date) => {
    const copy = new Date(date);
    copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
    return copy.toISOString().slice(0, 10);
  };

  const applyDateConstraints = () => {
    if (!startDateField || !endDateField) return;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minStart = toLocalISODate(tomorrow);

    if (startDateField.value && startDateField.value < minStart) {
      startDateField.setCustomValidity('Kies een startdatum in de toekomst.');
    } else {
      startDateField.setCustomValidity('');
    }

    const minEnd = startDateField.value || minStart;

    if (endDateField.value && endDateField.value < minEnd) {
      endDateField.setCustomValidity('De einddatum moet op of na de startdatum liggen.');
    } else {
      endDateField.setCustomValidity('');
    }
  };

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
   * @param {string} value
   * @returns {string}
   */
  const formatFieldValue = (field, value) => {
    if (!(field instanceof HTMLInputElement) || field.type !== 'date' || !value) {
      return value;
    }

    const date = new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      dateStyle: 'long',
    }).format(date);
  };

  /**
   * @param {string[]} [cartItems]
   * @returns {QuoteSnapshot}
   */
  const getFormSnapshot = (cartItems = []) => {
    /** @type {QuoteSnapshotField[]} */
    const fields = [];

    if (!quoteForm) return { fields, items: [] };

    const visibleFields = quoteForm.querySelectorAll(
      'input:not([type="hidden"]):not([hidden]), select, textarea:not([hidden])'
    );

    for (const field of visibleFields) {
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      if (field.id === 'QuoteCartDetails') continue;

      const rawValue =
        field instanceof HTMLSelectElement
          ? field.options[field.selectedIndex]?.text?.trim() || field.value
          : field.value.trim();

      if (!rawValue) continue;

      const label = quoteForm.querySelector(`label[for="${field.id}"]`);
      const labelText = label?.textContent?.trim() || '';

      fields.push({
        label: labelText,
        value: formatFieldValue(field, rawValue),
      });
    }

    const itemsFromFields = [...quoteForm.querySelectorAll('.js-quote-item-field')]
      .map((input) => (input instanceof HTMLInputElement ? input.value : ''))
      .filter(Boolean);

    const items = cartItems.length ? cartItems : itemsFromFields;

    return { fields, items };
  };

  /**
   * @param {QuoteSnapshot} snapshot
   */
  const persistSnapshot = (snapshot) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...snapshot,
          savedAt: Date.now(),
        })
      );
    } catch (error) {
      // Ignore quota / private-mode failures.
    }
  };

  /**
   * @returns {QuoteSnapshot | null}
   */
  const readSnapshot = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);

      if (!parsed || !Array.isArray(parsed.fields)) return null;

      if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt > SNAPSHOT_TTL_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return {
        fields: parsed.fields,
        items: Array.isArray(parsed.items) ? parsed.items : [],
      };
    } catch (error) {
      return null;
    }
  };

  const clearSnapshot = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Ignore storage failures.
    }
  };

  const stripContactPostedParam = () => {
    const url = new URL(window.location.href);

    if (!url.searchParams.has('contact_posted')) return;

    url.searchParams.delete('contact_posted');
    history.replaceState({}, '', url);
  };

  /**
   * @param {QuoteSnapshot} snapshot
   */
  const showConfirmation = (snapshot) => {
    latestSnapshot = snapshot;

    if (formFields) formFields.hidden = true;
    if (confirmation) confirmation.hidden = false;
    if (downloadPdfButton) downloadPdfButton.hidden = false;

    const confirmationTitle = title?.dataset.confirmationTitle;

    if (title && confirmationTitle) {
      title.textContent = confirmationTitle;
    }

    if (confirmationDetails) {
      confirmationDetails.replaceChildren();

      const hasDetails = snapshot.fields.length > 0 || snapshot.items.length > 0;
      confirmationDetails.hidden = !hasDetails;

      for (const field of snapshot.fields) {
        const row = document.createElement('div');
        row.className = 'quote-form__confirmation-row';

        const dt = document.createElement('dt');
        dt.className = 'quote-form__confirmation-label';
        dt.textContent = field.label;

        const dd = document.createElement('dd');
        dd.className = 'quote-form__confirmation-value';
        dd.textContent = field.value;

        row.append(dt, dd);
        confirmationDetails.append(row);
      }

      if (snapshot.items.length) {
        for (const [index, item] of snapshot.items.entries()) {
          const row = document.createElement('div');
          row.className = 'quote-form__confirmation-row';

          const dt = document.createElement('dt');
          dt.className = 'quote-form__confirmation-label';
          dt.textContent =
            index === 0 ? confirmationDetails.dataset.itemsLabel || '' : '';

          const dd = document.createElement('dd');
          dd.className = 'quote-form__confirmation-value';
          dd.textContent = item;

          row.append(dt, dd);
          confirmationDetails.append(row);
        }
      }
    }

    confirmation?.focus();
  };

  /**
   * @param {boolean} submitting
   */
  const setSubmitting = (submitting) => {
    if (!quoteForm) return;

    quoteForm.dataset.quoteSubmitting = submitting ? 'true' : 'false';

    const submitButton = quoteForm.querySelector('[type="submit"]');

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = submitting;
    }
  };

  /**
   * @param {QuoteCartItem} item
   * @returns {string}
   */
  const formatCartItem = (item) => {
    const quantity = item.quantity;
    const productTitle = item.product_title || item.title || 'Onbekend product';
    const variantTitle =
      item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

    return `${productTitle}${variantTitle ? ` (${variantTitle})` : ''} x ${quantity}`;
  };

  /**
   * Syncs cart line items into hidden contact fields and returns their labels.
   * @returns {Promise<string[]>}
   */
  const populateCartDetails = async () => {
    /** @type {string[]} */
    const itemValues = [];

    if (!quoteForm) return itemValues;

    try {
      const response = await fetch('/cart.js');
      const rawCart = await response.json();

      /** @type {QuoteCart} */
      const cart = rawCart;

      if (!cart || !Array.isArray(cart.items)) return itemValues;

      const existingItemFields = quoteForm.querySelectorAll('.js-quote-item-field');

      for (const field of existingItemFields) {
        field.remove();
      }

      /** @type {HTMLTextAreaElement | null} */
      const cartDetailsField = quoteForm.querySelector('#QuoteCartDetails');

      for (const [index, item] of cart.items.entries()) {
        const value = formatCartItem(item);
        itemValues.push(value);

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `contact[cart_item_${index + 1}]`;
        input.value = value;
        input.classList.add('js-quote-item-field');

        quoteForm.appendChild(input);
      }

      if (cartDetailsField) {
        cartDetailsField.value = itemValues.join('\n');
      }
    } catch (error) {
      // Cart details are supplementary; the form can still submit.
    }

    return itemValues;
  };

  const refreshCartUi = () => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: {}, sourceId: 'quote-form', data: { itemCount: 0 } },
      })
    );
  };

  const clearCart = async () => {
    try {
      const response = await fetch('/cart/clear.js', { method: 'POST' });
      await response.json();

      const cartIcon = document.querySelector('cart-icon');

      if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        cartIcon.renderCartBubble(0, false, false);
      }

      refreshCartUi();
    } catch (error) {
      // Ignore cart-clear failures; the quote was already sent.
    }
  };

  /**
   * Submits the contact form into a hidden iframe so the page does not reload.
   * @returns {Promise<void>}
   */
  const submitWithoutReload = () => {
    if (!quoteForm || !(submitFrame instanceof HTMLIFrameElement)) {
      quoteForm?.submit();
      return Promise.reject(new Error('Missing quote submit frame'));
    }

    quoteForm.setAttribute('target', submitFrame.name);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        submitFrame.removeEventListener('load', onLoad);
        reject(new Error('Quote request timed out'));
      }, SUBMIT_TIMEOUT_MS);

      const onLoad = () => {
        clearTimeout(timeoutId);
        resolve();
      };

      submitFrame.addEventListener('load', onLoad, { once: true });
      quoteForm.submit();
    });
  };

  /**
   * @param {SubmitEvent} event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!quoteForm) return;

    if (quoteForm.dataset.quoteSubmitting === 'true') return;

    applyDateConstraints();

    if (!quoteForm.checkValidity()) {
      quoteForm.reportValidity();
      return;
    }

    if (errorMessage) errorMessage.hidden = true;

    setSubmitting(true);

    try {
      const cartItems = await populateCartDetails();
      const snapshot = getFormSnapshot(cartItems);
      persistSnapshot(snapshot);
      await submitWithoutReload();
      showConfirmation(snapshot);
      clearSnapshot();
      await clearCart();
    } catch (error) {
      if (errorMessage) errorMessage.hidden = false;
      setSubmitting(false);
    }
  };

  if (startDateField) {
    startDateField.addEventListener('input', () => {
      applyDateConstraints();
      if (endDateField?.value) endDateField.reportValidity();
    });
    startDateField.addEventListener('change', () => {
      applyDateConstraints();
      if (endDateField?.value) endDateField.reportValidity();
    });
  }

  if (endDateField) {
    endDateField.addEventListener('input', applyDateConstraints);
    endDateField.addEventListener('change', applyDateConstraints);
  }

  if (quoteForm) {
    quoteForm.addEventListener('submit', handleSubmit, { capture: true });
  }

  if (downloadPdfButton) {
    downloadPdfButton.addEventListener('click', () => {
      if (!latestSnapshot) return;
      downloadQuotePdf(latestSnapshot);
    });
  }

  applyDateConstraints();
  void populateCartDetails();

  const posted = Boolean(quoteForm?.querySelector('[data-quote-posted]'));
  const snapshot = readSnapshot();

  if (posted || snapshot) {
    showConfirmation(snapshot || { fields: [], items: [] });
    clearSnapshot();
    stripContactPostedParam();
    void clearCart();
  }

  root.dataset.quoteFormInitialized = 'true';
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuoteForm);
} else {
  initQuoteForm();
}
